import { EditorContent, useEditor } from "@tiptap/react";
import { generateHTML } from "@tiptap/core";
import unescape from "lodash/unescape";

import { PropsWithChildren, useEffect, useMemo } from "react";
import { extensions } from "./extensions";
import { IKeywords } from "./extensions/Keywords";
import { EditorActions } from "./EditorActions";
import { EditorProvider } from "./EditorProvider";

interface EditorProps {
  initialValue?: string;
  disabled?: boolean;
  keywords?: IKeywords;
  className?: string;
  unescapeHtml?: boolean;

  onChange?(value: string): void;
}

export function Editor({
  initialValue,
  disabled,
  keywords,
  onChange,
  className,
  unescapeHtml,
  children,
}: PropsWithChildren<EditorProps>) {
  const content = useMemo(
    () => (unescapeHtml ? unescape(initialValue) : initialValue),
    [initialValue, unescapeHtml],
  );

  const editor = useEditor({
    extensions,
    content,
    editable: !disabled,
    async onUpdate(props) {
      if (!props.transaction.docChanged) {
        return;
      }

      const html = generateHTML(props.transaction.doc.toJSON(), extensions);

      onChange?.(html);
    },
  });

  useEffect(() => {
    // remove empty keywords as they break the editor
    const keywordsToSet = {
      final: keywords?.final?.filter((g) => g.length > 0),
    };

    editor?.chain().setKeywords(keywordsToSet).run();
  }, [keywords, editor]);

  return (
    <div className={className}>
      <EditorProvider editor={editor}>
        <div className="h-full flex flex-col relative">
          <EditorContent
            editor={editor}
            className="prose px-4 max-w-none overflow-y-auto flex flex-1"
            data-testid="editor"
          />
          <EditorActions editor={editor} />
          {children}
        </div>
      </EditorProvider>
    </div>
  );
}
